export type ThreadFilterType = 'inbox' | 'conversations'

export type ThreadFilterable = {
  kind?: string
  routingParticipantId?: string | null
  campaignRecipient?: boolean
  campaignThreadId?: string | null
  participants: Array<{
    kind: string
    memberId?: string | null
    displayName?: string
  }>
}

export function isCampaignRecipientThread(
  thread: Pick<ThreadFilterable, 'campaignRecipient' | 'campaignThreadId'>,
): boolean {
  return thread.campaignRecipient === true || !!thread.campaignThreadId
}

export type ThreadSearchable = ThreadFilterable & {
  title?: string | null
  lastMessagePreview?: string | null
  routingPattern?: string | null
  routingPatternDisplay?: string | null
  routingAddress?: string | null
  isExclusiveFeed?: boolean
}

export function isFeedThread(
  thread: Pick<ThreadSearchable, 'isExclusiveFeed' | 'routingPattern'>,
): boolean {
  if (thread.isExclusiveFeed) return true
  return !!thread.routingPattern
}

export function getFeedRoutingLabel(
  thread: Pick<ThreadSearchable, 'routingPatternDisplay' | 'routingAddress'>,
): string | null {
  if (thread.routingPatternDisplay) {
    return normalizeWildcardPatternDisplay(thread.routingPatternDisplay)
  }
  return thread.routingAddress ?? null
}

function normalizeWildcardPatternDisplay(display: string): string {
  const trimmed = display.trim()
  if (!trimmed.startsWith('*')) return trimmed

  const rest = trimmed.slice(1)
  if (rest.startsWith('@')) return trimmed

  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(rest)) {
    return `*@${rest}`
  }

  return trimmed
}

/** Client-side thread search (title, preview, participants, routing pattern). */
export function threadMatchesSearch(
  thread: ThreadSearchable,
  searchQuery: string,
): boolean {
  const query = searchQuery.trim().toLowerCase()
  if (!query) return true

  const haystacks: string[] = []
  if (thread.title) haystacks.push(thread.title)
  if (thread.lastMessagePreview) haystacks.push(thread.lastMessagePreview)
  if (thread.routingPattern) haystacks.push(thread.routingPattern)
  if (thread.routingPatternDisplay) {
    haystacks.push(thread.routingPatternDisplay)
  }
  if (thread.routingAddress) haystacks.push(thread.routingAddress)

  for (const participant of thread.participants) {
    if (participant.displayName) {
      haystacks.push(participant.displayName)
    }
  }

  return haystacks.some((value) => value.toLowerCase().includes(query))
}

export function isCurrentMemberThreadParticipant(
  thread: ThreadFilterable,
  currentMemberId: string | null,
): boolean {
  if (!currentMemberId) return false

  return thread.participants.some(
    (participant) =>
      participant.kind === 'MEMBER' && participant.memberId === currentMemberId,
  )
}

/** Inbox: threads where the current member is an active participant. */
export function isThreadInInbox(
  thread: ThreadFilterable,
  currentMemberId: string | null,
): boolean {
  if (thread.kind === 'TESTING') return false
  if (isCampaignRecipientThread(thread)) return false
  return isCurrentMemberThreadParticipant(thread, currentMemberId)
}

/** Conversations: external threads with a routing participant. */
export function isThreadInConversations(thread: ThreadFilterable): boolean {
  if (thread.kind === 'TESTING') return false
  if (isCampaignRecipientThread(thread)) return false
  return !!thread.routingParticipantId
}

/** Picks the default sidebar tab when navigating to a thread (not list filtering). */
export function classifyThreadFilter(
  thread: ThreadFilterable,
  currentMemberId: string | null,
): ThreadFilterType {
  if (isThreadInInbox(thread, currentMemberId)) return 'inbox'
  if (isThreadInConversations(thread)) return 'conversations'
  return 'inbox'
}
